import React, { useState, useRef, useEffect } from 'react';
import {
    Create,
    TextInput,
    TabbedForm,
    FormTab,
    ReferenceInput,
    SelectInput,
    required,
    minLength,
    maxLength,
    // regex,
    choices,
    BooleanInput,
    DateTimeInput,
    FormDataConsumer,
} from 'react-admin';
import {
    Button,
    Typography,
    FormHelperText,
} from '@material-ui/core';
import { getUserURL } from '../api';
import dataProvider from '../dataProvider';
import SearchableSelect from '../common/SearchableSelect';
import { change } from 'redux-form';
import AddressAutoComplete from '../users/AddressAutoComplete';

const validateStoreName = [required(), minLength(2), maxLength(50)];
const validateZipCode = [required()];
//const validateZipCode = [required(), regex(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, 'Must be a valid Zip Code')];
const validateCountry = [required()];
const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const defaultBusinessHours = daysOfWeek.reduce((acc, day) => {
    acc[day] = { open: true, open_time: '09:00', close_time: '18:00' };
    return acc;
}, {});

// Custom State Select Component
const StateSelect = ({ source, label, validate }) => {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchStates = async () => {
            setLoading(true);
            try {
                const response = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {}
                });
                setStates(response.data);
            } catch (error) {
                console.error('Failed to fetch states:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStates();
    }, []);

    return (
        <FormDataConsumer>
            {({ formData, dispatch }) => {
                const value = formData && formData.address && formData.address.state ? formData.address.state : '';
                
                return (
                    <SearchableSelect
                        value={value}
                        onChange={(e) => {
                            dispatch(change('record-form', source, e.target.value));
                        }}
                        options={states}
                        label={label}
                        disabled={loading}
                        error={false}
                        helperText={undefined}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.name}
                        placeholder="Search state..."
                        allowCustom={false}
                    />
                );
            }}
        </FormDataConsumer>
    );
};

// Custom City Select Component
const CitySelect = ({ source, label, validate }) => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedState, setSelectedState] = useState('');

    useEffect(() => {
        const fetchCities = async () => {
            if (!selectedState) {
                setCities([]);
                return;
            }
            setLoading(true);
            try {
                // First get the state ID from state name
                const statesResponse = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {}
                });
                const state = statesResponse.data.find(s => s.name === selectedState);
                
                if (state) {
                    const response = await dataProvider('GET_LIST', `cities/state/${state.id}`, {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'name', order: 'ASC' },
                        filter: {}
                    });
                    setCities(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch cities:', error);
                setCities([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCities();
    }, [selectedState]);

    return (
        <FormDataConsumer>
            {({ formData, dispatch }) => {
                const currentState = formData && formData.address && formData.address.state;
                if (currentState !== selectedState) {
                    setSelectedState(currentState);
                }
                
                const value = formData && formData.address && formData.address.city ? formData.address.city : '';
                
                return (
                    <SearchableSelect
                        value={value}
                        onChange={(e) => {
                            dispatch(change('record-form', source, e.target.value));
                        }}
                        options={cities}
                        label={label}
                        disabled={!selectedState || loading}
                        error={false}
                        helperText={undefined}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.name}
                        placeholder="Search city..."
                    />
                );
            }}
        </FormDataConsumer>
    );
};

const LocationCreate = props => {
    const [businessHours, setBusinessHours] = useState(defaultBusinessHours);

    // 添加照片上传相关状态
    const [storePhoto, setStorePhoto] = useState(null);
    const [photoError, setPhotoError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    // 添加表单引用
    const formRef = useRef();

    // 当 businessHours 或 storePhoto 改变时，更新表单数据
    useEffect(() => {
        if (formRef.current) {
            // // 将 businessHours 对象转换为数组格式
            // const businessHoursArray = daysOfWeek.map(day => ({
            //     week_day: day,
            //     open: businessHours[day].open,
            //     open_time: businessHours[day].openTime,
            //     close_time: businessHours[day].closeTime
            // }));
            // formRef.current.setValue('businessHours', businessHoursArray);

            // 设置照片路径
            if (storePhoto) {
                formRef.current.setValue('storePhoto', storePhoto.path);
            }
        }
    }, [businessHours, storePhoto]);

    const handleCheckboxChange = (day) => (e) => {
        setBusinessHours({
            ...businessHours,
            [day]: {
                ...businessHours[day],
                open: e.target.checked,
            }
        });
    };

    const handleTimeChange = (day, field) => (e) => {
        setBusinessHours({
            ...businessHours,
            [day]: {
                ...businessHours[day],
                [field]: e.target.value,
            }
        });
    };

    const handleImageUpload = async (file) => {
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);

        try {
            const baseUrl = getUserURL();
            const response = await fetch(`${baseUrl}/v1/files/image`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('BITFA:DASHBOARD:AUTH:TOKEN')}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const filePath = data.filePath;

            setStorePhoto({
                url: data.fileUrl,
                path: filePath
            });

            setPhotoError('');
        } catch (error) {
            console.error('Failed to upload image:', error);
            setPhotoError('Image upload failed, please try again');
        } finally {
            setUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleImageUpload(files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const removePhoto = () => {
        setStorePhoto(null);
        setPhotoError('');
    };

    return (
        <Create title="Location" {...props} redirect="list">
            <TabbedForm ref={formRef}>
                <FormTab label="resources.locations.tabs.info">
                    <ReferenceInput label="Staff" source="staff_id" reference="user/staffs" validate={required()}>
                        <SelectInput optionText="name" />
                    </ReferenceInput>
                    <TextInput
                        source="nickName" label="Store Name" validate={validateStoreName}
                    />
                    {/* <TextInput
                        source="email" validate={validateEmail} label="Email"
                    />
                    <TextInput
                        source="phoneNumber" validate={validatePhoneNumber}
                    /> */}
                    <TextInput
                        source="description"
                    />
                    {/* <TextInput
                        source="hours"
                    /> */}
                </FormTab>
                <FormTab label="resources.accounts.tabs.address">
                    <TextInput source="address.address2" label="Unit Number" validate={required()} />
                    <TextInput source="address.address1" label="Street Address" validate={required()} />
                    <StateSelect source="address.state" label="State" validate={required()} />
                    <CitySelect source="address.city" label="City" validate={required()} />
                    <TextInput source="address.postalCode" label="Postal Code" validate={validateZipCode} />
                </FormTab>
                {/* <FormTab label="resources.accounts.tabs.doc">
                    <FileInput source="files" label="Business proof. For example, business license and certificate of coorpation." accept="application/pdf" multiple={true} maxSize={1000000}>
                        <FileField source="base64" title="title" />
                    </FileInput>
                </FormTab> */}
                {/* add business hours in the new tab add checkbox for non-close days and two inputs for each day */}
                <FormTab label="Business Hours">
                    {daysOfWeek.map(day => (
                        <div
                            key={day}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                margin: 20,
                                width: '100%'
                            }}
                        >
                            <BooleanInput
                                id={`businessHours_${day}_open`}
                                defaultValue={businessHours[day].open}
                                source={`businessHours_${day}_open`}
                                label={day}
                                style={{ marginRight: 12 }}
                            />
                            <DateTimeInput
                                type="time"
                                label="Open Time"
                                defaultValue={businessHours[day].open_time}
                                id={`businessHours_${day}_open_time`}
                                source={`businessHours_${day}_open_time`}
                                parse={v => {
                                    return v; 
                                }}
                                format={v => {
                                    return v;
                                }}
                                style={{ width: 120, marginRight: 12 }}
                            />
                            <DateTimeInput
                                type="time"
                                label="Close Time"
                                defaultValue={businessHours[day].close_time}
                                id={`businessHours_${day}_close_time`}
                                source={`businessHours_${day}_close_time`}
                                 parse={v => {
                                    return v; 
                                }}
                                format={v => {
                                    return v;
                                }}
                                style={{ width: 120 }}
                            />
                        </div>
                    ))}

                    {/* 隐藏的输入字段，用于存储 businessHours 数据 */}
                    {/* <TextInput
                        source="businessHours"
                        style={{ display: 'none' }}
                        defaultValue={daysOfWeek.map(day => ({
                            week_day: day,
                            open: businessHours[day].open,
                            open_time: businessHours[day].openTime,
                            close_time: businessHours[day].closeTime
                        }))}
                    /> */}
                </FormTab>

                <FormTab label="Store Photo">
                    <div style={{
                        border: `2px dashed ${dragOver ? '#1976d2' : '#e0e0e0'}`,
                        borderRadius: '8px',
                        padding: '24px',
                        textAlign: 'center',
                        backgroundColor: dragOver ? '#f3f8ff' : '#fafafa',
                        transition: 'all 0.3s ease',
                        marginBottom: '16px',
                        position: 'relative'
                    }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                    >
                        {!storePhoto ? (
                            <>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="store-photo-upload"
                                    type="file"
                                    onChange={(e) => handleImageUpload(e.target.files[0])}
                                    disabled={uploading}
                                />
                                <label htmlFor="store-photo-upload" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <div style={{
                                            fontSize: '48px',
                                            color: '#bdbdbd',
                                            marginBottom: '8px'
                                        }}>
                                            📷
                                        </div>
                                        <Typography variant="h6" style={{
                                            color: '#757575',
                                            marginBottom: '4px'
                                        }}>
                                            {uploading ? 'Uploading...' : 'Click or drag to upload store photo'}
                                        </Typography>
                                        <Typography variant="body2" style={{
                                            color: '#9e9e9e'
                                        }}>
                                            Support JPG, PNG formats, recommended size 1200x800
                                        </Typography>
                                        {!uploading && (
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                style={{
                                                    marginTop: '8px',
                                                    borderColor: '#1976d2',
                                                    color: '#1976d2'
                                                }}
                                            >
                                                Choose File
                                            </Button>
                                        )}
                                        {uploading && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginTop: '8px'
                                            }}>
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    border: '2px solid #f3f3f3',
                                                    borderTop: '2px solid #1976d2',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                <Typography variant="body2" color="primary">
                                                    Uploading...
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div style={{
                                    position: 'relative',
                                    display: 'inline-block'
                                }}>
                                    <img
                                        src={storePhoto.url}
                                        alt="Store photo"
                                        style={{
                                            maxWidth: '300px',
                                            maxHeight: '200px',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                            objectFit: 'cover',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                        }}
                                        onClick={() => window.open(storePhoto.url, '_blank')}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'scale(1.02)';
                                            e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'scale(1)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                        }}
                                        title="Click to view full size image"
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        left: '8px',
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        fontSize: '12px',
                                        opacity: 0.8
                                    }}>
                                        🔍 Click to enlarge
                                    </div>
                                    <Button
                                        onClick={removePhoto}
                                        style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            minWidth: '32px',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            fontSize: '14px',
                                            padding: '0'
                                        }}
                                    >
                                        ×
                                    </Button>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Typography variant="body2" style={{
                                        color: '#4caf50',
                                        marginBottom: '8px'
                                    }}>
                                        ✓ Photo uploaded successfully
                                    </Typography>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="store-photo-replace"
                                        type="file"
                                        onChange={(e) => handleImageUpload(e.target.files[0])}
                                    />
                                    <label htmlFor="store-photo-replace">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            size="small"
                                        >
                                            Replace Photo
                                        </Button>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    {photoError && (
                        <div style={{
                            backgroundColor: '#ffebee',
                            border: '1px solid #f44336',
                            borderRadius: '4px',
                            padding: '12px',
                            marginTop: '8px'
                        }}>
                            <Typography variant="body2" style={{ color: '#f44336' }}>
                                ⚠️ {photoError}
                            </Typography>
                        </div>
                    )}

                    {/* CSS动画样式 */}
                    <style jsx>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>

                    {/* 隐藏的输入字段，用于存储照片路径 */}
                    <TextInput
                        source="storePhoto"
                        style={{ display: 'none' }}
                        defaultValue={storePhoto ? storePhoto.path : ''}
                    />
                </FormTab>
            </TabbedForm>
        </Create>
    );
};

export default LocationCreate;
